import React, { useEffect, useState } from "react";
import { get, post } from "../api";
import { errText, getLang, t } from "../i18n";
import { navigate, toast, useStore } from "../store";
import { Avatar, Button, Coins, GameArt, Link, useAsync } from "../ui";

export function Home() {
  const me = useStore((s) => s.me)!;
  const balance = useStore((s) => s.balance);
  const games = useAsync(() => get("/api/games"));
  const friends = useAsync(() => get("/api/friends"));
  const news = useAsync(() => get("/api/announcements"));
  const board = useAsync(() => get("/api/leaderboard?period=weekly"));
  const mine = useAsync(() => get("/api/me/rooms"));
  const notes = useAsync(() => get("/api/notifications"));
  const [resent, setResent] = useState(false);
  const invites = (notes.data?.notifications ?? []).filter((n: any) => n.kind === "room_invite" && !n.read_at).slice(0, 3);
  const online = (friends.data?.friends ?? []).filter((f: any) => f.online);
  const current = mine.data?.rooms?.[0];

  return (
    <div className="home">
      {!me.emailVerified && (
        <div className="banner">
          <span>{t.verifyEmailBanner}</span>
          <Button kind="quiet" disabled={resent} onClick={async () => { await post("/api/auth/resend-verification"); setResent(true); }}>
            {resent ? t.sent : t.resend}
          </Button>
        </div>
      )}
      {current && (
        <Link to={`/room/${current.id}`} className="resume">
          <GameArt id={current.game_id} />
          <span>
            <strong>{t.game[current.game_id]}</strong>
            <small>{current.status === "playing" ? t.playing : t.waiting}</small>
          </span>
          <span className="btn btn-primary">{t.play}</span>
        </Link>
      )}
      <section className="table" aria-label={t.games}>
        {(games.data?.games ?? []).map((g: any) => (
          <Link key={g.id} to={`/games/${g.id}`} className="game-tile">
            {g.is_new && <span className="tag">{t.newBadge}</span>}
            <GameArt id={g.id} />
            <strong>{getLang() === "ar" ? g.name_ar : g.name_en}</strong>
            <span>{t.gameBlurb[g.id]}</span>
          </Link>
        ))}
      </section>
      <aside className="home-side">
        <section className="panel">
          <header className="panel-head">
            <h2>{t.level} {me.level}</h2>
            <Link to="/wallet"><Coins n={balance?.total ?? 0} /></Link>
          </header>
          <div className="xpbar" aria-hidden="true">
            <span style={{ width: `${Math.min(100, ((me.xp - (me.level - 1) ** 2 * 100) / ((me.level ** 2 - (me.level - 1) ** 2) * 100)) * 100)}%` }} />
          </div>
        </section>
        {invites.length > 0 && (
          <section className="panel">
            <h2>{t.invitations}</h2>
            <ul className="list">
              {invites.map((n: any) => (
                <li key={n.id} className="list-row">
                  <span>{t.notif.room_invite(n.payload)} — {t.game[n.payload.gameId]}</span>
                  <Button
                    kind="ghost"
                    onClick={async () => {
                      try {
                        const r = await post("/api/rooms/join", { code: n.payload.code });
                        navigate(`/room/${r.room.id}`);
                      } catch (e) {
                        toast(errText(e), "error");
                      }
                    }}
                  >
                    {t.join}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="panel">
          <header className="panel-head">
            <h2>{t.friendsOnline}</h2>
            <Link to="/friends">{t.seeAll}</Link>
          </header>
          {online.length ? (
            <ul className="avatars-row">
              {online.slice(0, 8).map((f: any) => (
                <li key={f.id}>
                  <Link to={`/u/${f.username}`}>
                    <Avatar name={f.displayName} url={f.avatarUrl} online size={44} />
                    <small>{f.displayName}</small>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{friends.data?.friends?.length ? t.offline : t.noFriends}</p>
          )}
        </section>
        <section className="panel">
          <header className="panel-head">
            <h2>{t.leaderboard} · {t.weekly}</h2>
            <Link to="/leaderboard">{t.seeAll}</Link>
          </header>
          {board.data?.rows?.length ? (
            <ol className="rank">
              {board.data.rows.slice(0, 5).map((r: any) => (
                <li key={r.user_id} className={r.user_id === me.id ? "is-me" : ""}>
                  <Avatar name={r.display_name} url={r.avatar_url} size={28} />
                  <Link to={`/u/${r.username}`}>{r.display_name}</Link>
                  <bdi>{r.points}</bdi>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted">{t.noRanking}</p>
          )}
        </section>
        {news.data?.announcements?.length > 0 && (
          <section className="panel">
            <h2>{t.announcements}</h2>
            {news.data.announcements.slice(0, 3).map((a: any) => (
              <article key={a.id} className={`news news-${a.kind}`}>
                <h3>{getLang() === "ar" ? a.title_ar : a.title_en}</h3>
                <p>{getLang() === "ar" ? a.body_ar : a.body_en}</p>
              </article>
            ))}
          </section>
        )}
      </aside>
    </div>
  );
}

export function useInterval(fn: () => void, ms: number) {
  useEffect(() => {
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  }, [fn, ms]);
}
